// pages/Channels/Whatsapp/BulkWhatsappSender/BulkWhatsappSender.js
import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import Papa from 'papaparse';
import { 
    Stepper, Step, StepLabel, Typography, Button, Box, IconButton, 
    Input, Card, CardContent, Grid, List, ListItem, ListItemText, 
    Divider, Badge, TextField, Alert 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SendIcon from '@mui/icons-material/Send';

function BulkWhatsappSenderChannel() {
    const [activeStep, setActiveStep] = useState(0);
    const [csvData, setCsvData] = useState([]);
    const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
    const [messageTemplate, setMessageTemplate] = useState('');
    const [templateName, setTemplateName] = useState('');
    const [responseMessage, setResponseMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const fileInputRef = useRef(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [sendStatusMessages, setSendStatusMessages] = useState([]);

    const { chatbotId: paramsChatbotId } = useParams();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('channels', 'view');
                const modifyPerm = await hasPermission('channels', 'add|edit');
                const deletePerm = await hasPermission('channels', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
                setCanDelete(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);
    
    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            Papa.parse(file, {
                header: true,
                delimiter: ',',
                newline: '\r\n',
                skipEmptyLines: true,
                complete: (results) => {
                    if (results.errors.length > 0) {
                        setErrorMessage("Error parsing CSV: " + results.errors.map(e => e.message).join(", "));
                        setCsvData([]);
                        setCsvHeaders([]);
                    } else if (results.data && results.data.length > 0) {
                        setCsvData(results.data);
                        setErrorMessage('');
                        setCsvHeaders(results.meta.fields || []);
                    } else {
                        setErrorMessage("No data found in CSV file or incorrect format.");
                        setCsvData([]);
                        setCsvHeaders([]);
                    }
                },
                error: (error) => {
                    setErrorMessage("Error reading CSV file: " + error.message);
                    setCsvData([]);
                    setCsvHeaders([]);
                }
            });
        }
    };

    const handleUploadToServer = async () => {
        setResponseMessage('');
        setErrorMessage('');
        if (fileInputRef.current.files && fileInputRef.current.files[0]) {
            const formData = new FormData();
            formData.append('csv_file', fileInputRef.current.files[0]);

            try {
                const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};                
                const response = await axiosInstance.post(`/upload-csv`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    params
                });
                if (response.status >= 200 && response.status < 300) {
                    setResponseMessage(response.data.message);
                    fetchRecipients();
                    fileInputRef.current.value = '';
                    handleNext();
                } else {
                    if (response.data && response.data.message) {
                        setErrorMessage(response.data.message);
                    } else {
                        setErrorMessage(`Upload failed with status: ${response.status}`);
                    }
                }
            } catch (error) {
                if (error.response && error.response.data && error.response.data.message) {
                    setErrorMessage(error.response.data.message);
                    console.error("Error uploading CSV:", error.response.data.message);
                } else {
                    setErrorMessage('Failed to upload CSV file. (Network error)');
                    console.error("Error uploading CSV:", error);
                }
            }
        } else {
            setErrorMessage("Please select a CSV file to upload.");
        }
    };

    const fetchRecipients = async () => {
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/get-recipients`, { params });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            setCsvData(responseData.recipients);
        } catch (error) {
            setErrorMessage("Error fetching recipients.");
            console.error("Error fetching recipients:", error);
        }
    };

    const handleRecipientSelection = (event) => {
        const recipientId = parseInt(event.target.value);
        if (event.target.checked) {
            setSelectedRecipientIds([...selectedRecipientIds, recipientId]);
        } else {
            setSelectedRecipientIds(selectedRecipientIds.filter(id => id !== recipientId));
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allIds = csvData.map(recipient => recipient.id);
            setSelectedRecipientIds(allIds);
        } else {
            setSelectedRecipientIds([]);
        }
    };

    const handleTemplateChange = (e) => {
        setMessageTemplate(e.target.value);
    };

    const handleTemplateNameChange = (e) => {
        setTemplateName(e.target.value);
    };

    const handleSaveTemplate = async () => {
        setResponseMessage('');
        setErrorMessage('');
        if (!templateName) {
            setErrorMessage("Please enter a template name.");
            return;
        }
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.post(`/save-template`, { 
                template_name: templateName, 
                template_content: messageTemplate 
            }, { params });
            if (response.status >= 200 && response.status < 300) {
                setResponseMessage(response.data.message);
                fetchTemplate();
            } else {
                setErrorMessage(response.data.message || `Error: ${response.status}`);
            }
        } catch (error) {
            setErrorMessage('Failed to save message template.');
            console.error("Error saving template:", error);
        }
    };

    const fetchTemplate = async () => {
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/get-template`, { params });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            if (responseData) {
                setMessageTemplate(responseData.template_content);
                setTemplateName(responseData.template_name);
            } else {
                setMessageTemplate('');
                setTemplateName('');
            }
        } catch (error) {
            setErrorMessage("Error fetching message template.");
            console.error("Error fetching template:", error);
        }
    };

    const handleSendMessage = async () => {
        setResponseMessage('');
        setErrorMessage('');
        setSendStatusMessages([]);

        if (selectedRecipientIds.length === 0) {
            setErrorMessage("Please select recipients to send messages to.");
            return;
        }
        if (!messageTemplate) {
            setErrorMessage("Please enter a message template.");
            return;
        }

        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.post(`/send-bulk-message`, {
                recipient_ids: selectedRecipientIds,
                message_template: messageTemplate,
                template_name: templateName
            }, { params });
            
            if (response.status >= 200 && response.status < 300) {
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

                setResponseMessage(responseData.message + ` Success: ${responseData.success_count}, Errors: ${responseData.error_count}`);
                
                const detailedStatuses = [];
                if (responseData.errors_detail && Array.isArray(responseData.errors_detail)) {
                    responseData.errors_detail.forEach(errorDetail => {
                        detailedStatuses.push({
                            name: errorDetail.recipient_name,
                            number: errorDetail.recipient_number,
                            status: 'failed'
                        });
                    });
                }
                
                selectedRecipientIds.forEach(recipientId => {
                    const recipient = csvData.find(r => r.id === recipientId);
                    if (recipient && !responseData.errors_detail?.some(err => err.recipient_name === recipient.name)) {
                        detailedStatuses.push({
                            name: recipient.name,
                            number: recipient.whatsapp_number,
                            status: 'success'
                        });
                    }
                });
                setSendStatusMessages(detailedStatuses);
            } else {
                setErrorMessage(response.data.message || `Error: ${response.status}`);
            }
        } catch (error) {
            setErrorMessage('Failed to send bulk messages.');
            console.error("Error sending bulk messages:", error);
        }
    };

    const handleSkipUpload = () => {
        setActiveStep(1);
    };

    useEffect(() => {
        if (canView) {
            fetchRecipients();
            fetchTemplate();
        }
    }, [canView]);

    // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <div>
                <main className="content">
                    <div className="container-fluid p-0">
                        <Alert severity="error" sx={{ mt: 6 }}>
                            <strong>Access Denied</strong>
                            <br />
                            You don't have permission to access Bulk WhatsApp Sender. Please contact your administrator.
                        </Alert>
                    </div>
                </main>
            </div>
        );
    }

    const steps = ['Upload file', 'Select Recipients', 'Message Template', 'Send Bulk Messages'];
    
    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3">
                        <h1 className="h3 d-inline align-middle"> <strong>Bulk WhatsApp </strong> Sender</h1>
                    </div>
                    <div className="row">
                        <div className="col-12 col-lg-12">
                            <div className="card">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Upload CSV and Send Bulk WhatsApp Messages</h5>
                                </div>
                                <div className="card-body">
                                    <Box sx={{ mt: 2 }}>
                                        <Stepper activeStep={activeStep} alternativeLabel>
                                            {steps.map((label, index) => (
                                                <Step key={label}>
                                                    <StepLabel>{label}</StepLabel>
                                                </Step>
                                            ))}
                                        </Stepper>

                                        {activeStep === 0 && (
                                            <Box>
                                                <Box sx={{ 
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', 
                                                    p: 3, border: '1px dashed grey', borderRadius: '8px', 
                                                    cursor: canModify ? 'pointer' : 'not-allowed', 
                                                    '&:hover': canModify ? { borderColor: 'primary.main', borderStyle: 'solid' } : {}, 
                                                    mb: 2,
                                                    opacity: canModify ? 1 : 0.6
                                                }}
                                                    onClick={() => canModify && fileInputRef.current.click()}
                                                >
                                                    <IconButton color="primary" component="span" disabled={!canModify}>
                                                        <CloudUploadIcon sx={{ fontSize: 60, color: 'grey' }} />
                                                    </IconButton>
                                                    <Typography variant="subtitle1" color="text.secondary">
                                                        {canModify ? 'Drag and drop your import file here' : 'Upload disabled - No modify permission'}
                                                    </Typography>
                                                    {canModify && (
                                                        <>
                                                            <Button variant="text" onClick={() => fileInputRef.current.click()} startIcon={<InsertDriveFileIcon />}>
                                                                Browse files
                                                            </Button>
                                                            <Typography variant="caption" color="text.secondary">
                                                                File format must be .xlsx or .csv
                                                            </Typography>
                                                        </>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept=".csv, .xlsx"
                                                        onChange={handleFileUpload}
                                                        ref={fileInputRef}
                                                        style={{ display: 'none' }}
                                                        disabled={!canModify}
                                                    />
                                                </Box>
                                                <Button variant="outlined" onClick={handleSkipUpload} disabled={!canModify}>
                                                    Skip File Upload
                                                </Button>
                                            </Box>
                                        )}

                                        {activeStep === 1 && (
                                            <div>
                                                {csvData.length > 0 && (
                                                    <div>
                                                        <h4>Select Recipients</h4>
                                                        <div className="table-responsive">
                                                            <table className="table table-striped">
                                                                <thead>
                                                                    <tr>
                                                                        <th>
                                                                            <input 
                                                                                type="checkbox" 
                                                                                onChange={handleSelectAll} 
                                                                                disabled={!canModify}
                                                                            /> Select All
                                                                        </th>
                                                                        <th>Name</th>
                                                                        <th>WhatsApp Number</th>
                                                                        {csvHeaders.map((header, index) => (
                                                                            <th key={index}>{header}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {csvData.map((recipient, index) => (
                                                                        <tr key={index}>
                                                                            <td>
                                                                                <input 
                                                                                    type="checkbox" 
                                                                                    value={recipient.id} 
                                                                                    checked={selectedRecipientIds.includes(recipient.id)} 
                                                                                    onChange={handleRecipientSelection}
                                                                                    disabled={!canModify}
                                                                                />
                                                                            </td>
                                                                            <td>{recipient.name}</td>
                                                                            <td>{recipient.whatsapp_number}</td>
                                                                            {csvHeaders.map((header, index) => (
                                                                                <td key={index}>
                                                                                    {recipient.data ? recipient.data[header] : 'N/A'}
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                                {!csvData.length && activeStep === 1 && (
                                                    <Typography>Please upload a CSV file in the first step to select recipients.</Typography>
                                                )}
                                            </div>
                                        )}

                                        {activeStep === 2 && (
                                            <div className="mt-3">
                                                <h4>Message Template</h4>
                                                <TextField
                                                    label="Template Name"
                                                    fullWidth
                                                    value={templateName}
                                                    onChange={handleTemplateNameChange}
                                                    margin="normal"
                                                    disabled={!canModify}
                                                />
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    Use personalization variables like {'{Name}'}, {'{amount}'}, {'{address}'} etc.
                                                </Typography>
                                                <textarea
                                                    className="form-control"
                                                    rows="4"
                                                    placeholder="Enter your message template here (use {Name}, {amount}, {address} etc. for personalization)"
                                                    value={messageTemplate}
                                                    onChange={handleTemplateChange}
                                                    disabled={!canModify}
                                                />
                                                {canModify && (
                                                    <Button variant="contained" className='mt-2' onClick={handleSaveTemplate}>
                                                        Save Template
                                                    </Button>
                                                )}
                                            </div>
                                        )}

                                        {activeStep === 3 && (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h5" gutterBottom>
                                                        Confirm and Send Messages
                                                    </Typography>
                                                    <Divider sx={{ mb: 2 }} />
                                                    <List>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={<Typography variant="subtitle1">Recipients Selected</Typography>}
                                                                secondary={<Typography variant="body1">{selectedRecipientIds.length}</Typography>}
                                                            />
                                                        </ListItem>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={<Typography variant="subtitle1">Message Template Preview</Typography>}
                                                                secondary={<Typography variant="body1">{messageTemplate ? messageTemplate.substring(0, 100) + '...' : 'No template set'}</Typography>}
                                                            />
                                                        </ListItem>
                                                    </List>
                                                    <Divider sx={{ mt: 2, mb: 2 }} />
                                                    <Box mt={2} display="flex" justifyContent="space-between">
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleBack}
                                                        >
                                                            Back
                                                        </Button>
                                                        {canModify && (
                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                onClick={handleSendMessage}
                                                                disabled={selectedRecipientIds.length === 0 || !messageTemplate}
                                                                endIcon={<SendIcon />}
                                                            >
                                                                <Typography variant="subtitle2" sx={{ color: 'white' }}>Send Bulk Messages</Typography>
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        )}

                                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                            <Button
                                                color="inherit"
                                                disabled={activeStep === 0}
                                                onClick={handleBack}
                                                sx={{ mr: 1 }}
                                            >
                                                Back
                                            </Button>
                                            <Box sx={{ flexGrow: 1 }} />
                                            {activeStep === 0 && canModify && (
                                                <Button variant="contained" onClick={handleUploadToServer} disabled={!fileInputRef.current?.files?.length}>
                                                    Upload CSV
                                                </Button>
                                            )}
                                            {activeStep === 1 && (
                                                <Button variant="contained" onClick={handleNext} disabled={csvData.length === 0}>
                                                    Next
                                                </Button>
                                            )}
                                            {activeStep === 2 && (
                                                <Button variant="contained" onClick={handleNext} disabled={!messageTemplate}>
                                                    Next
                                                </Button>
                                            )}
                                        </Box>

                                        {activeStep === 3 && sendStatusMessages.length > 0 && (
                                            <Box mt={3}>
                                                <Typography variant="h6" gutterBottom>Sending Status:</Typography>
                                                <Grid container spacing={2}>
                                                    {sendStatusMessages.map((status, index) => (
                                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                                            <Card variant="outlined">
                                                                <CardContent style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Box>
                                                                        <Typography variant="subtitle2" gutterBottom>{status.name}</Typography>
                                                                        <Typography variant="body2" color="textSecondary">{status.number}</Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Badge
                                                                            badgeContent={status.status.toUpperCase()}
                                                                            color={status.status === 'success' ? 'success' : 'error'}
                                                                        />
                                                                    </Box>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        )}

                                        <br />
                                        {responseMessage && <div className="alert alert-info">{responseMessage}</div>}
                                        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                                    </Box>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default BulkWhatsappSenderChannel;